
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useWorksheetSearch, Worksheet } from '@/hooks/useWorksheetSearch';
import { Link, ExternalLink, SearchIcon, FileText } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';

const WorksheetSearchPage: React.FC = () => {
  const { t } = useTranslation();
  const { worksheets, isLoading, searchQuery, setSearchQuery } = useWorksheetSearch();
  const [showHelp, setShowHelp] = useState(true);

  useEffect(() => {
    // Hide help text after a search has been performed
    if (searchQuery.length > 0) {
      setShowHelp(false);
    }
  }, [searchQuery]);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Form submission is handled by the input's onChange which updates searchQuery
  };

  const getStatusColor = (status: string | null) => {
    if (!status) return 'bg-gray-400';
    switch (status.toLowerCase()) {
      case 'approved': return 'bg-green-500';
      case 'pending': return 'bg-yellow-500';
      case 'rejected': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  return (
    <div className="container max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">{t('worksheet.pageTitle')}</h1>
      
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle>{t('worksheet.cardTitle')}</CardTitle>
          <CardDescription>
            {t('worksheet.cardDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="flex flex-col md:flex-row gap-2">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder={t('worksheet.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full"
                  autoFocus
                />
              </div>
              <Button type="submit" className="flex items-center gap-2">
                <SearchIcon className="h-4 w-4" />
                {t('worksheet.search')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {showHelp && (
        <Card className="mb-6 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-2">
              <FileText className="h-6 w-6 text-blue-500 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-blue-800">{t('worksheet.helpTitle')}</h3>
                <ul className="list-disc pl-5 mt-2 space-y-1 text-blue-700">
                  <li>{t('worksheet.help1')}</li>
                  <li>{t('worksheet.help2')}</li>
                  <li>{t('worksheet.help3')}</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results Section */}
      {searchQuery.length > 1 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">{t('worksheet.results')}</h2>
            {isLoading ? (
              <Badge className="bg-blue-500">{t('worksheet.searching')}</Badge>
            ) : (
              <Badge className="bg-gray-500">{worksheets.length} {t('worksheet.items')}</Badge>
            )}
          </div>

          {isLoading ? (
            <Card className="p-8 text-center">
              <div className="flex flex-col items-center justify-center">
                <div className="animate-spin h-8 w-8 border-4 border-blue-500 rounded-full border-t-transparent"></div>
                <p className="mt-4 text-gray-500">{t('worksheet.loading')}</p>
              </div>
            </Card>
          ) : worksheets.length > 0 ? (
            <div className="grid gap-4">
              {worksheets.map((worksheet) => (
                <Card key={worksheet.id} className="overflow-hidden">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{worksheet.name}</CardTitle>
                        <CardDescription className="mt-1">
                          <span className="text-gray-500">{t('worksheet.subject')}: {worksheet.subject} | </span>
                          <span className="text-gray-500">{t('worksheet.grade')}: {worksheet.grade_level}</span>
                          {worksheet.semester && (
                            <span className="text-gray-500"> | {t('worksheet.term')}: {worksheet.semester}</span>
                          )}
                        </CardDescription>
                      </div>
                      {worksheet.status && (
                        <Badge className={`${getStatusColor(worksheet.status)}`}>
                          {worksheet.status}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Separator className="my-2" />
                    <div className="mt-3">
                      <div className="space-y-1">
                        {worksheet.fs_name && (
                          <p className="text-sm text-gray-600">{t('worksheet.shop')}: {worksheet.fs_name}</p>
                        )}
                        <div className="mt-3">
                          {worksheet.document_urls && worksheet.document_urls.length > 0 ? (
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium flex items-center gap-1.5">
                                <Link className="h-4 w-4 text-blue-500" />
                                {t('worksheet.docLinks')}
                              </h4>
                              <ul className="space-y-1 pl-6 list-disc">
                                {worksheet.document_urls.map((url, idx) => (
                                  <li key={idx} className="text-sm">
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:underline flex items-center gap-1"
                                    >
                                      {url.length > 60 ? `${url.substring(0, 60)}...` : url}
                                      <ExternalLink className="h-3 w-3" />
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic">{t('worksheet.noLinks')}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-8 text-center bg-gray-50">
              <div className="flex flex-col items-center justify-center">
                <FileText className="h-12 w-12 text-gray-300" />
                <h3 className="mt-4 text-lg font-medium text-gray-700">{t('worksheet.notFound')}</h3>
                <p className="mt-2 text-gray-500 max-w-md">
                  {t('worksheet.notFoundDesc', { query: searchQuery })}
                </p>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default WorksheetSearchPage;
